/* context/ContextProvider.js */
"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import finishLogin from "@/helpers/finishLogin";
import {
  clearLogoutInProgress,
  isLogoutInProgress,
} from "@/helpers/logoutClient";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { usePathname, useRouter } from "next/navigation";
import { getFirebaseAuth, getFirestoreDb } from "@/lib/firebaseClient";
import { isCookieConsentSuppressedRoute } from "@/constants/noTrackingRoutes";

export const UserContext = createContext(null);
export const useAuth = () => useContext(UserContext);

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function runWhenIdle(fn, { timeoutMs = 2000, fallbackDelayMs = 1000 } = {}) {
  if (typeof window === "undefined") return () => {};
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(fn, { timeout: timeoutMs });
    return () => window.cancelIdleCallback?.(id);
  }
  const t = setTimeout(fn, fallbackDelayMs);
  return () => clearTimeout(t);
}

export function UserProvider({
  children,
  initialUser = null,
  initialCookieConsentAccepted = false,
  disableBackground = false,
}) {
  const pathname = usePathname();
  const effectiveDisableBackground =
    disableBackground || isCookieConsentSuppressedRoute(pathname);
  const isLeadLandingRoute =
    pathname === "/" ||
    pathname.startsWith("/faq") ||
    pathname.startsWith("/ratgeber") ||
    pathname.startsWith("/auto-bewerten") ||
    pathname.startsWith("/auto-verkaufen") ||
    pathname.startsWith("/fahrgestellnummer-pruefen") ||
    pathname.startsWith("/kaputtes-auto-verkaufen");

  /* --------------------------------------------------
   * Consent-Toggles
   * --------------------------------------------------*/
  const [isEssentialChecked, setIsEssentialChecked] = useState(true);
  const [isAnalyticalChecked, setIsAnalyticalChecked] = useState(
    effectiveDisableBackground ? false : !!readCookie("analytical")
  );
  const [isMarketingChecked, setIsMarketingChecked] = useState(
    effectiveDisableBackground ? false : !!readCookie("marketing")
  );
  const [isCookiePopupOpen, setIsCookiePopupOpen] = useState(
    effectiveDisableBackground ? false : !initialCookieConsentAccepted
  );
  // If SSR already confirms consent via cookie, render immediately.
  // Otherwise wait for client-side storage check to avoid one-frame modal flash.
  const [isCookieConsentResolved, setIsCookieConsentResolved] = useState(
    effectiveDisableBackground ? true : initialCookieConsentAccepted
  );

  /* --------------------------------------------------
   * UI-Hilfen
   * --------------------------------------------------*/
  useEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowX = "hidden";
    }
  }, []);

  useEffect(() => {
    if (effectiveDisableBackground) {
      setIsAnalyticalChecked(false);
      setIsMarketingChecked(false);
      setIsCookiePopupOpen(false);
      setIsCookieConsentResolved(true);
      return;
    }

    let cancelled = false;
    let retryTimer = null;

    const resolveConsent = (attempt = 0) => {
      if (cancelled || typeof window === "undefined") return;

      let hasConsent = initialCookieConsentAccepted;
      try {
        const consent = window.localStorage.getItem("cookieConsentAccepted");
        if (consent === "true") hasConsent = true;
        setIsCookiePopupOpen(!hasConsent);
        setIsCookieConsentResolved(true);
      } catch {
        // If storage is temporarily unavailable, retry a few times.
        if (attempt < 10) {
          retryTimer = window.setTimeout(() => resolveConsent(attempt + 1), 50);
          return;
        }
        setIsCookiePopupOpen(!hasConsent);
        setIsCookieConsentResolved(true);
      }
    };

    resolveConsent();

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [effectiveDisableBackground, initialCookieConsentAccepted]);

  /* --------------------------------------------------
   * Auth-State (startet mit Server-Side User)
   * --------------------------------------------------*/
  const [userData, setUserData] = useState(initialUser);
  const [loggedIn, setLoggedIn] = useState(!!initialUser);
  // Default: don't block first paint on Firebase boot; hydrate auth state lazily.
  const [loading, setLoading] = useState(false);
  // Separate from visual loading: persistent per-user queues must not hydrate
  // under a temporary null owner before Firebase resolves the first auth state.
  const [authResolved, setAuthResolved] = useState(Boolean(initialUser));
  const userDataRef = useRef(initialUser);
  const hadAuthUserRef = useRef(!!initialUser);
  const finishLoginInFlightRef = useRef(false);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    if (effectiveDisableBackground) return;

    let unsubSnap = null;
    let unsubAuth = null;
    let missingUserDocSignOutTimer = null;
    let authStateGapTimer = null;

    let cancelled = false;
    let started = false;

    const clearMissingUserDocTimer = () => {
      if (missingUserDocSignOutTimer !== null) {
        window.clearTimeout(missingUserDocSignOutTimer);
        missingUserDocSignOutTimer = null;
      }
    };
    const clearAuthStateGapTimer = () => {
      if (authStateGapTimer !== null) {
        window.clearTimeout(authStateGapTimer);
        authStateGapTimer = null;
      }
    };

    const startFirebaseListeners = async () => {
      try {
        const auth = await getFirebaseAuth();

        unsubAuth = auth.onAuthStateChanged(async (current) => {
          if (cancelled) return;
          setAuthResolved(true);
          clearAuthStateGapTimer();

          // ✅ cleanup previous snapshot when auth user changes
          if (typeof unsubSnap === "function") {
            unsubSnap();
            unsubSnap = null;
          }
          clearMissingUserDocTimer();

          // ✅ signed out
          if (!current) {
            // Avoid random single-tab logouts from transient auth gaps.
            if (hadAuthUserRef.current || userDataRef.current) {
              setLoading(true);
              authStateGapTimer = window.setTimeout(() => {
                if (cancelled) return;
                if (!auth.currentUser) {
                  setUserData(null);
                  setLoggedIn(false);
                  setLoading(false);
                  clearLogoutInProgress();
                }
                authStateGapTimer = null;
              }, 800);
              return;
            }
            setUserData(null);
            setLoggedIn(false);
            setLoading(false);
            clearLogoutInProgress();
            return;
          }
          hadAuthUserRef.current = true;

          // Only show auth-loading when we do not already have hydrated user data.
          // Otherwise this causes a visible "second loading flash" on profile flows.
          if (!userDataRef.current) {
            setLoading(true);
          }

          const db = await getFirestoreDb();
          const { doc, getDoc, getDocFromServer, onSnapshot } = await import(
            "firebase/firestore"
          );
          const ref = doc(db, "Users", current.email);

          // Public lead pages: avoid long-lived realtime listeners in the critical path.
          if (isLeadLandingRoute) {
            try {
              const snap = await getDoc(ref);

              if (!snap.exists()) {
                setUserData(null);
                setLoggedIn(false);
                setLoading(false);
                auth.signOut();
                return;
              }

              const user = snap.data();
              if (user?.isAdmin) {
                setUserData(null);
                setLoggedIn(false);
                setLoading(false);
                auth.signOut();
                return;
              }

              setUserData(user);
              setLoggedIn(true);
              setLoading(false);
            } catch {
              setUserData(null);
              setLoggedIn(false);
              setLoading(false);
            }
            return;
          }

          unsubSnap = onSnapshot(
            ref,
            (snap) => {
              if (!snap.exists()) {
                // Newly created users can briefly miss the Users/{email} doc due to
                // backend write timing. Keep auth alive for a short grace period.
                setLoading(true);
                if (missingUserDocSignOutTimer !== null) return;
                missingUserDocSignOutTimer = window.setTimeout(async () => {
                  if (cancelled) return;
                  try {
                    // Verify against server before forcing logout to avoid
                    // random tab-specific cache/offline false negatives.
                    const verifySnap = await getDocFromServer(ref);
                    if (verifySnap.exists()) {
                      missingUserDocSignOutTimer = null;
                      setLoading(false);
                      return;
                    }
                  } catch (verifyErr) {
                    console.warn(
                      "[UserProvider] Users doc verify skipped:",
                      verifyErr?.code || verifyErr?.message || verifyErr
                    );
                    // Network/transient issue: keep current auth state.
                    missingUserDocSignOutTimer = null;
                    setLoading(false);
                    return;
                  }
                  setUserData(null);
                  setLoggedIn(false);
                  setLoading(false);
                  auth.signOut();
                  missingUserDocSignOutTimer = null;
                }, 7000);
                return;
              }

              clearMissingUserDocTimer();
              const user = snap.data();
              if (user?.isAdmin) {
                setUserData(null);
                setLoggedIn(false);
                setLoading(false);
                auth.signOut();
                return;
              }

              setUserData(user);
              setLoggedIn(true);
              setLoading(false);
            },
            (snapErr) => {
              clearMissingUserDocTimer();
              console.warn(
                "[UserProvider] Users snapshot listener error:",
                snapErr?.code || snapErr?.message || snapErr
              );
              // Keep existing session on transient listener errors.
              if (!userDataRef.current) {
                setUserData(null);
                setLoggedIn(false);
              }
              setLoading(false);
            }
          );
        });
      } catch (e) {
        // Don't crash the app if Firebase can't init (privacy modes, blocked storage, etc.)
        console.warn("[UserProvider] Firebase init skipped:", e?.message || e);
        setAuthResolved(true);
        setLoading(false);
      }
    };

    const ensureStarted = () => {
      if (started || cancelled) return;
      started = true;
      startFirebaseListeners();
    };

    // Defer bootstrap only on public lead pages without an SSR user.
    // On account/profil flows we must initialize auth immediately to avoid
    // transient "logged out" states and notFound redirects after signup.
    const deferMore = !initialUser && isLeadLandingRoute;

    let cancelDeferredStart = () => {};
    if (deferMore) {
      const t = setTimeout(ensureStarted, 30000);
      cancelDeferredStart = () => clearTimeout(t);
    } else {
      ensureStarted();
    }

    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
    ];
    const interactionOptions = { passive: true };
    if (deferMore && typeof window !== "undefined") {
      for (const evt of interactionEvents) {
        window.addEventListener(evt, ensureStarted, interactionOptions);
      }
    }

    return () => {
      cancelled = true;
      clearMissingUserDocTimer();
      clearAuthStateGapTimer();
      if (typeof unsubSnap === "function") unsubSnap();
      if (typeof unsubAuth === "function") unsubAuth();
      for (const evt of interactionEvents) {
        window.removeEventListener(evt, ensureStarted, interactionOptions);
      }
      cancelDeferredStart?.();
    };
  }, [effectiveDisableBackground, initialUser, isLeadLandingRoute]);

  const api = useBackendUrl();
  const router = useRouter();

  /* Falls eingeloggt, aber Cookie noch fehlt → finishLogin() */
  useEffect(() => {
    if (effectiveDisableBackground) return;

    // Magic-link route handles session cookie + redirect on its own.
    // Running finishLogin here can strip query params and trigger a
    // transient /anmelden hop before the final target route.
    if (pathname?.startsWith("/magic-link")) return;
    if (pathname?.startsWith("/logout")) return;
    if (isLogoutInProgress()) return;

    const hasSessionFrontCookie =
      typeof document !== "undefined" &&
      document.cookie.includes("__session_front=");
    const cookieEmail = readCookie("__session_front_email")
      .trim()
      .toLowerCase();
    const userEmail = String(userData?.email || "")
      .trim()
      .toLowerCase();
    const hasCookieEmail = cookieEmail.length > 0;
    const hasUserEmail = userEmail.length > 0;
    const hasEmailMismatch =
      hasCookieEmail && hasUserEmail && cookieEmail !== userEmail;

    if (
      loggedIn &&
      typeof document !== "undefined" &&
      (!hasSessionFrontCookie || !hasCookieEmail || hasEmailMismatch)
    ) {
      if (finishLoginInFlightRef.current) return;

      finishLoginInFlightRef.current = true;
      (async () => {
        try {
          await finishLogin(api, router); // setzt Cookies & reloadet Seite
        } finally {
          finishLoginInFlightRef.current = false;
        }
      })();
    }
  }, [effectiveDisableBackground, loggedIn, userData?.email, api, router, pathname]);

  /* -------------------------------------------------- */
  return (
    <UserContext.Provider
      value={{
        // Consent
        isEssentialChecked,
        setIsEssentialChecked,
        isAnalyticalChecked,
        setIsAnalyticalChecked,
        isMarketingChecked,
        setIsMarketingChecked,
        isCookiePopupOpen,
        isCookieConsentResolved,
        setIsCookiePopupOpen,
        // Auth
        userData,
        loggedIn,
        loading,
        authResolved,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
